// ============================================================
//  src/App.js
// ============================================================
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { setNavigate } from './context/NavigationContext';
import ErrorBoundary from './components/common/ErrorBoundary';

// Layout (always loaded — needed immediately)
import AppLayout from './components/layout/AppLayout';

// Landing + Auth (small, load eagerly — user lands here before login)
import LandingPage        from './pages/landing/LandingPage';
import LoginPage          from './pages/auth/LoginPage';
import RegisterPage       from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyEmailPage    from './pages/auth/VerifyEmailPage';

// ── Lazy-loaded pages (only downloaded when visited) ──────────

// Dashboard
const DashboardPage     = lazy(() => import('./pages/dashboard/DashboardPage'));
const NotificationsPage = lazy(() => import('./pages/dashboard/NotificationsPage'));

// Wallet
const TransactionsPage = lazy(() => import('./pages/wallet/TransactionsPage'));
const FundWalletPage   = lazy(() => import('./pages/wallet/FundWalletPage'));

// Tasks
const TasksPage           = lazy(() => import('./pages/tasks/TasksPage'));
const PlaceOrderPage      = lazy(() => import('./pages/tasks/PlaceOrderPage'));
const MyOrdersPage        = lazy(() => import('./pages/tasks/MyOrdersPage'));
const OrderDetailPage     = lazy(() => import('./pages/tasks/OrderDetailPage'));
const ContentServicesPage = lazy(() => import('./pages/tasks/ContentServicesPage'));

// Earner (largest pages — most important to lazy-load)
const EarnerDashboardPage  = lazy(() => import('./pages/earner/EarnerDashboardPage'));
const EarnerTasksPage      = lazy(() => import('./pages/earner/EarnerTasksPage'));
const EarnerTaskDetailPage = lazy(() => import('./pages/earner/EarnerTaskDetailPage'));
const BecomeEarnerPage     = lazy(() => import('./pages/earner/BecomeEarnerPage'));
const WithdrawPage         = lazy(() => import('./pages/earner/WithdrawPage'));
const EarnerHistoryPage    = lazy(() => import('./pages/earner/EarnerHistoryPage'));

// Profile
const ProfilePage  = lazy(() => import('./pages/profile/ProfilePage'));
const ReferralPage = lazy(() => import('./pages/profile/ReferralPage'));
const PinPage      = lazy(() => import('./pages/profile/PinPage'));

// Contact (your new page)
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));

// ── Page loading fallback ─────────────────────────────────────

function PageFallback() {
  return (
    <div className="page-loader" style={{ minHeight: '100vh' }}>
      <div className="spinner spinner-dark" />
    </div>
  );
}

// ── Wires React Router's navigate into the axios interceptor ──

function RouterSetter() {
  const navigate = useNavigate();
  useEffect(() => { setNavigate(navigate); }, [navigate]);
  return null;
}

// ── Guards ────────────────────────────────────────────────────

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

// ── App ───────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouterSetter />
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: '13.5px',
              fontWeight: 600,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              padding: '12px 20px',
              maxWidth: '90vw',
            },
          }}
        />
        <Routes>
          {/* Landing — guests see it, logged-in users go to /dashboard */}
          <Route path="/"               element={<LandingRoute />} />

          {/* Public auth */}
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/verify-email"    element={<VerifyEmailPage />} />

          {/* Protected app shell — ErrorBoundary wraps the whole shell */}
          <Route element={
            <PrivateRoute>
              <ErrorBoundary>
                <AppLayout />
              </ErrorBoundary>
            </PrivateRoute>
          }>
            <Route path="dashboard"        element={<Suspense fallback={<PageFallback />}><DashboardPage /></Suspense>} />
            <Route path="transactions"     element={<Suspense fallback={<PageFallback />}><TransactionsPage /></Suspense>} />
            <Route path="fund-wallet"      element={<Suspense fallback={<PageFallback />}><FundWalletPage /></Suspense>} />
            <Route path="notifications"    element={<Suspense fallback={<PageFallback />}><NotificationsPage /></Suspense>} />
            <Route path="tasks"            element={<Suspense fallback={<PageFallback />}><TasksPage /></Suspense>} />
            <Route path="tasks/order/:id"  element={<Suspense fallback={<PageFallback />}><PlaceOrderPage /></Suspense>} />
            <Route path="my-orders"        element={<Suspense fallback={<PageFallback />}><MyOrdersPage /></Suspense>} />
            <Route path="my-orders/:id"    element={<Suspense fallback={<PageFallback />}><OrderDetailPage /></Suspense>} />
            <Route path="content-services" element={<Suspense fallback={<PageFallback />}><ContentServicesPage /></Suspense>} />
            <Route path="earner"           element={<Suspense fallback={<PageFallback />}><EarnerDashboardPage /></Suspense>} />
            <Route path="earner/tasks"     element={<Suspense fallback={<PageFallback />}><EarnerTasksPage /></Suspense>} />
            <Route path="earner/tasks/:id" element={<Suspense fallback={<PageFallback />}><EarnerTaskDetailPage /></Suspense>} />
            <Route path="become-earner"    element={<Suspense fallback={<PageFallback />}><BecomeEarnerPage /></Suspense>} />
            <Route path="withdraw"         element={<Suspense fallback={<PageFallback />}><WithdrawPage /></Suspense>} />
            <Route path="earner/history"   element={<Suspense fallback={<PageFallback />}><EarnerHistoryPage /></Suspense>} />
            <Route path="profile"          element={<Suspense fallback={<PageFallback />}><ProfilePage /></Suspense>} />
            <Route path="referral"         element={<Suspense fallback={<PageFallback />}><ReferralPage /></Suspense>} />
            <Route path="pin"              element={<Suspense fallback={<PageFallback />}><PinPage /></Suspense>} />
            <Route path="contact"          element={<Suspense fallback={<PageFallback />}><ContactPage /></Suspense>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}